import { AreaClosed } from '@visx/shape';
import { Fragment, memo, useMemo } from 'react';
import { CurveData } from '~/domain/patient/dvh';
import { DvhPoint } from '~/utils/dvhCurve';
import { LinearScale } from './chartStyle';
import { linePathD } from './linePathD';

/** The most curves the chart fills the area under. */
const AREA_FILL_MAX_CURVES = 2;

/** Stroke opacity of a curve while another one is emphasized. */
const DIMMED_OPACITY = 0.3;

const xOf = (point: DvhPoint) => point.x;
const yOf = (point: DvhPoint) => point.y;

/** The pixel mapping of a linear scale as two numbers: `px = k * value + b`. */
function coefficientsOf(scale: LinearScale): { k: number; b: number } {
    const b = scale(0);
    return { k: scale(1) - b, b };
}

/**
 * One path per curve, plotted verbatim: the chart draws every histogram
 * point it is given, so the hover, which snaps to the nearest real point,
 * can only report a dose the DVH actually holds. With at most two curves
 * the area under each is filled. An emphasized structure keeps full weight
 * while the others dim. The path strings are built once per data or scale
 * change, so a hover over the chart costs no path work.
 */
function DvhCurves({
    curves,
    xScale,
    yScale,
    emphasizedRoiNumber,
}: {
    curves: CurveData[];
    xScale: LinearScale;
    yScale: LinearScale;
    emphasizedRoiNumber?: number;
}) {
    const fillArea = curves.length <= AREA_FILL_MAX_CURVES;
    // One mapping from a point to pixels serves the path strings and the
    // area fill, so the two can never disagree about where a point sits.
    const { toX, toY, paths } = useMemo(() => {
        const x = coefficientsOf(xScale);
        const y = coefficientsOf(yScale);
        const toX = (point: DvhPoint) => x.k * xOf(point) + x.b;
        const toY = (point: DvhPoint) => y.k * yOf(point) + y.b;
        return {
            toX,
            toY,
            paths: curves.map((curve) => linePathD(curve.data, toX, toY)),
        };
    }, [curves, xScale, yScale]);
    return (
        <g data-testid="dvh-curves">
            {curves.map((curve, index) => {
                const dimmed =
                    emphasizedRoiNumber !== undefined &&
                    curve.roiNumber !== emphasizedRoiNumber;
                return (
                    <Fragment key={curve.name}>
                        {fillArea && !dimmed ? (
                            <AreaClosed
                                data={curve.data}
                                x={toX}
                                y={toY}
                                yScale={yScale}
                                fill={curve.color}
                                fillOpacity={0.5}
                                data-testid="dvh-area"
                            />
                        ) : null}
                        <path
                            d={paths[index]}
                            stroke={curve.color}
                            strokeWidth={dimmed ? 1 : 1.5}
                            strokeOpacity={dimmed ? DIMMED_OPACITY : 1}
                            fill="none"
                            data-testid="dvh-curve-path"
                        />
                    </Fragment>
                );
            })}
        </g>
    );
}

export default memo(DvhCurves);
